import { Station } from 'dataSources/Typicode/stations/types';
import { BBox } from '@turf/helpers';
import turfBbox from '@turf/bbox';
import { reduceAsync, isActionItem, turfLineString } from 'common/utils';

export interface MappingStationResult {
  stations: Station[];
  actionStations: Station[];
  stationsBBox: BBox;
  actionStationsBBox: BBox;
  serviceCodes: number[];
}

interface ReduceResult
  extends Pick<
    MappingStationResult,
    'stations' | 'actionStations' | 'serviceCodes'
  > {
  stationCoords: [
    Station['coordinates']['longitude'],
    Station['coordinates']['latitude'],
  ][];
  actionStationCoords: [
    Station['coordinates']['longitude'],
    Station['coordinates']['latitude'],
  ][];
}

export const mappingStations = async (
  stations: Station[],
): Promise<MappingStationResult> => {
  const result = await reduceAsync<Station, ReduceResult>(
    stations,
    (acc, station) => {
      if (station.code && station.coordinates) {
        const newItem = {
          ...station,
          roadsideServiceCodes: station.roadsideServiceCodes || [],
        };

        acc.stations.push(newItem);
        acc.stationCoords.push([
          station.coordinates.longitude,
          station.coordinates.latitude,
        ]);

        newItem.roadsideServiceCodes.forEach((code) => {
          if (!acc.serviceCodes.has(code)) {
            acc.serviceCodes.set(code);
          }
        });

        if (isActionItem(newItem)) {
          acc.actionStations.push(newItem);
          acc.actionStationCoords.push([
            station.coordinates.longitude,
            station.coordinates.latitude,
          ]);
        }
      }
      return acc;
    },
    {
      stations: [],
      actionStations: [],
      stationCoords: [],
      actionStationCoords: [],
      serviceCodes: new Map(),
    },
  );

  return {
    ...result,
    stationsBBox: turfBbox(turfLineString(result.stationCoords)),
    actionStationsBBox: turfBbox(
      turfLineString(
        result.actionStationCoords.length > 2
          ? result.actionStationCoords
          : [...result.actionStationCoords, ...result.actionStationCoords],
      ),
    ),

    serviceCodes: Array.from(result.serviceCodes.keys()),
  };
};
